import * as Yup from 'yup';
import _ from 'lodash';
import { isSequentialOrRepeatable } from '../utils/index';
import { CONSTANT } from '../utils/constant';
import { getLocalizedStrings } from '../misc';

const {
  KEYS: {
    CHANNELS: { DEALER, BCX_CARE, ADT, HEALTH, CSD, NBD, CORP_STORE }
  }
} = CONSTANT;

const isOutletRequired = (role, channel) =>
  +role !== 11 &&
  ![BCX_CARE, ADT, HEALTH, CSD, NBD, CORP_STORE].includes(
    _.get(channel, '0.id', '')
  );

const isProvinceRequired = role => +role !== 11;

const isCpmsRequired = (role, channel) =>
  +role !== 11 &&
  ![BCX_CARE, ADT, HEALTH, CSD, NBD, CORP_STORE].includes(
    _.get(channel, '0.id', '')
  );

const isDealershipRequired = (role, channel, multiChannels) => {
  const valueChannel =
    +role === 11 ? multiChannels : [_.get(channel, '0.id', '')];
  return valueChannel && valueChannel.includes(DEALER);
};

const isSalesManagerFieldRequired = channel =>
  _.get(channel, '0.id', '') === DEALER;

const isChannelRequired = role => ![8, 11].includes(+role);

const isXidRequired = (role, channel) =>
  +role === 11 ||
  [BCX_CARE, ADT, HEALTH, CSD, NBD, CORP_STORE].includes(
    _.get(channel, '0.id', '')
  );
const isStoreCoderequired = (role, channel, multiChannels) => {
  const valueChannel =
    +role === 11 ? multiChannels : [_.get(channel, '0.id', '')];
  return valueChannel && valueChannel.includes(CORP_STORE);
};

const RegisterAsSalesRepAndManager = Yup.object().shape({
  firstName: Yup.string().required(() => {
    const {
      USERS: {
        USER_AUTHENTICATION: {
          REGISTRATION_SCHEMA_MESSAGES: { FIRST_NAME_REQUIRED }
        }
      }
    } = getLocalizedStrings();
    return FIRST_NAME_REQUIRED;
  }),
  lastName: Yup.string().required(() => {
    const {
      USERS: {
        USER_AUTHENTICATION: {
          REGISTRATION_SCHEMA_MESSAGES: { LAST_LAST_REQUIRED }
        }
      }
    } = getLocalizedStrings();
    return LAST_LAST_REQUIRED;
  }),
  email: Yup.string()
    .email(() => {
      const {
        USERS: {
          USER_AUTHENTICATION: {
            REGISTRATION_SCHEMA_MESSAGES: { INVALID_EMAIL }
          }
        }
      } = getLocalizedStrings();
      return INVALID_EMAIL;
    })
    .required(() => {
      const {
        USERS: {
          USER_AUTHENTICATION: {
            REGISTRATION_SCHEMA_MESSAGES: { EMAIL_REQUIRED }
          }
        }
      } = getLocalizedStrings();
      return EMAIL_REQUIRED;
    }),
  password: Yup.string()
    .required(() => {
      const {
        USERS: {
          USER_AUTHENTICATION: {
            REGISTRATION_SCHEMA_MESSAGES: { PASSWORD_REQUIRED }
          }
        }
      } = getLocalizedStrings();
      return PASSWORD_REQUIRED;
    })
    .matches(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/, () => {
      const {
        USERS: {
          USER_AUTHENTICATION: {
            REGISTRATION_SCHEMA_MESSAGES: { PASSWORD_MATCHES }
          }
        }
      } = getLocalizedStrings();
      return PASSWORD_MATCHES;
    })
    .test(
      'password',
      () => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { PASSWORD_REPEAT }
            }
          }
        } = getLocalizedStrings();
        return PASSWORD_REPEAT;
      },
      isSequentialOrRepeatable
    ),
  province: Yup.mixed().when('role', {
    is: isProvinceRequired,
    then: Yup.string().required(() => {
      const {
        USERS: {
          USER_AUTHENTICATION: {
            REGISTRATION_SCHEMA_MESSAGES: { PROVINCE_REQUIRED }
          }
        }
      } = getLocalizedStrings();
      return PROVINCE_REQUIRED;
    })
  }),
  contactNumber: Yup.string()
    .required(() => {
      const {
        USERS: {
          USER_AUTHENTICATION: {
            REGISTRATION_SCHEMA_MESSAGES: { CONTACT_REQUIRED }
          }
        }
      } = getLocalizedStrings();
      return CONTACT_REQUIRED;
    })
    .matches(/^[0-9]+$/, () => {
      const {
        USERS: {
          USER_AUTHENTICATION: {
            REGISTRATION_SCHEMA_MESSAGES: { MIN_BE_DIGIT_10 }
          }
        }
      } = getLocalizedStrings();
      return MIN_BE_DIGIT_10;
    }),
  role: Yup.string().required(() => {
    const {
      USERS: {
        USER_AUTHENTICATION: {
          REGISTRATION_SCHEMA_MESSAGES: { SELECT_ROLE }
        }
      }
    } = getLocalizedStrings();
    return SELECT_ROLE;
  }),
  channel: Yup.mixed().when('role', {
    is: isChannelRequired,
    then: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string(),
          label: Yup.string()
        })
      )
      .required(() => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { CHANNEL_REQUIRED }
            }
          }
        } = getLocalizedStrings();
        return CHANNEL_REQUIRED;
      })
  }),
  outlet: Yup.string().when(['role', 'channel'], {
    is: isOutletRequired,
    then: Yup.string()
      .required(() => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { OUTLET_REQUIRED }
            }
          }
        } = getLocalizedStrings();
        return OUTLET_REQUIRED;
      })
      .matches(/^[a-zA-Z0-9]+$/, () => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { ALPHANUMERIC }
            }
          }
        } = getLocalizedStrings();
        return ALPHANUMERIC;
      })
      .min(10, () => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { MUST_EXACTLY_10 }
            }
          }
        } = getLocalizedStrings();
        return MUST_EXACTLY_10;
      })
      .max(10, () => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { MAX_BE_DIGIT_10 }
            }
          }
        } = getLocalizedStrings();
        return MAX_BE_DIGIT_10;
      }),
    otherwise: Yup.string()
  }),
  cpmsId: Yup.string().when(['role', 'channel'], {
    is: isCpmsRequired,
    then: Yup.string()
      .required(() => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { CPMS_REQUIRED }
            }
          }
        } = getLocalizedStrings();
        return CPMS_REQUIRED;
      })
      .matches(/^[a-zA-Z0-9]+$/, () => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { ALPHANUMERIC }
            }
          }
        } = getLocalizedStrings();
        return ALPHANUMERIC;
      }),
    otherwise: Yup.string()
  }),
  dealership: Yup.string().when(['role', 'channel', 'multiChannels'], {
    is: isDealershipRequired,
    then: Yup.string().required(() => {
      const {
        USERS: {
          USER_AUTHENTICATION: {
            REGISTRATION_SCHEMA_MESSAGES: { DEALERSHIP_NAME_REQUIRED }
          }
        }
      } = getLocalizedStrings();
      return DEALERSHIP_NAME_REQUIRED;
    }),
    otherwise: Yup.string()
  }),
  channelOrgCode: Yup.string().when(['role', 'channel', 'multiChannels'], {
    is: isDealershipRequired,
    then: Yup.string()
      .required(() => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { CHANNEL_ORG_CODE_REQUIRED }
            }
          }
        } = getLocalizedStrings();
        return CHANNEL_ORG_CODE_REQUIRED;
      })
      .min(5, () => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { MAX_5_DIGIT }
            }
          }
        } = getLocalizedStrings();
        return MAX_5_DIGIT;
      })
      .max(5, () => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { MAX_5_DIGIT }
            }
          }
        } = getLocalizedStrings();
        return MAX_5_DIGIT;
      }),
    otherwise: Yup.string()
  }),
  xid: Yup.string().when(['role', 'channel'], {
    is: isXidRequired,
    then: Yup.string()
      .required(() => {
        return 'Please enter your XID/TID';
      })
      .min(7, () => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { MAX_7_DIGIT }
            }
          }
        } = getLocalizedStrings();
        return MAX_7_DIGIT;
      })
      .max(7, () => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { MAX_7_DIGIT }
            }
          }
        } = getLocalizedStrings();
        return MAX_7_DIGIT;
      }),
    otherwise: Yup.string()
  }),
  storeCode: Yup.string().when(['role', 'channel', 'multiChannels'], {
    is: isStoreCoderequired,
    then: Yup.string()
      .required(() => {
        return 'Please enter your store code';
      })
      .min(5, () => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { MAX_5_DIGIT }
            }
          }
        } = getLocalizedStrings();
        return MAX_5_DIGIT;
      })
      .max(5, () => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { MAX_5_DIGIT }
            }
          }
        } = getLocalizedStrings();
        return MAX_5_DIGIT;
      }),
    otherwise: Yup.string()
  }),
  salesManager: Yup.string().when('channel', {
    is: isSalesManagerFieldRequired,
    then: Yup.string(),
    otherwise: Yup.string().required(() => {
      const {
        USERS: {
          USER_AUTHENTICATION: {
            REGISTRATION_SCHEMA_MESSAGES: { SALES_MANAGER_REQUIRED }
          }
        }
      } = getLocalizedStrings();
      return SALES_MANAGER_REQUIRED;
    })
  }),
  salesManagerEmail: Yup.string().when('channel', {
    is: isSalesManagerFieldRequired,
    then: Yup.string(),
    otherwise: Yup.string()
      .required(() => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { SALES_MANAGER_EMAIL_REQUIRED }
            }
          }
        } = getLocalizedStrings();
        return SALES_MANAGER_EMAIL_REQUIRED;
      })
      .email(() => {
        const {
          USERS: {
            USER_AUTHENTICATION: {
              REGISTRATION_SCHEMA_MESSAGES: { INVALID_EMAIL }
            }
          }
        } = getLocalizedStrings();
        return INVALID_EMAIL;
      })
  })
});

export default RegisterAsSalesRepAndManager;
